import { checkEqual, checkIndex } from './core.ts';
import { recordFromEntries, type StringLiteral } from './identity.ts';

export type EnumValue = number | string;
export type EnumType<E extends EnumValue> = {
  [id: string]: E | string;
  [index: number]: string;
};

export function valuesOfEnum<E extends EnumValue>(enumType: EnumType<E>): E[] {
  return Object.keys(enumType)
    .filter((e) => Number.isNaN(parseInt(e, 10)))
    .map((n) => enumType[n] as E);
}

export class EnumValues<E extends EnumValue> {
  readonly names: string[];
  readonly values: E[];
  readonly valueNames: Map<E, string>;

  constructor(enumType: EnumType<E>) {
    this.names = Object.keys(enumType).filter((e) => Number.isNaN(parseInt(e, 10)));
    this.values = this.names.map((n) => enumType[n] as E);
    this.valueNames = new Map(this.names.map((n) => [enumType[n] as E, n]));
  }

  previous(value: E): E | undefined {
    const index = checkIndex(this.values.indexOf(value), this.values);
    return index === 0 ? undefined : this.values[index - 1];
  }

  next(value: E): E | undefined {
    const index = checkIndex(this.values.indexOf(value), this.values);
    return index === this.values.length - 1 ? undefined : this.values[index + 1];
  }
}

// Usage:
//   export const Foos = new StringEnum('foo', 'bar');
//   export type Foo = StringEnumType<typeof Foos>;
//   checkEqual('foo', Foos.values[Foos.indexOf['foo']]);
export class StringEnum<T extends string> {
  readonly values: readonly T[];
  readonly indexOf: Readonly<Record<T, number>>;

  // |values| must contain exactly one of each member of union T. This is easily done by inferring
  // the type from the array, but coverage of the range of T can't be verified.
  constructor(...values: readonly StringLiteral<T>[]) {
    this.values = Object.isFrozen(values) ? values : Object.freeze(values);
    this.indexOf = Object.freeze(
      recordFromEntries(this.values.map((v, i) => [v, i])) as Record<T, number>,
    );
    checkEqual(
      this.values.length,
      Object.keys(this.indexOf).length,
      'StringEnum has duplicate entries',
    );
  }

  has(v: unknown): v is T {
    return typeof v === 'string' && v in this.indexOf;
  }

  guard(): (v: unknown) => v is T {
    return (v) => this.has(v);
  }

  get length() {
    return this.values.length;
  }
  get first() {
    return this.values[0];
  }
  get last() {
    return this.values[this.values.length - 1];
  }
}

// By convention, this should be declared as the singular form of E.
// e.g. type Cat = StringEnumType<Cats>;
export type StringEnumType<E extends StringEnum<string>> =
  E extends StringEnum<infer T> ? T : never;
